import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { StepComponentProps } from "../../components/MultiStepGradeModal";
import { HomeworkIdeaCreateRequestDto } from "@packages/api/madlenAPI.schemas";

export const HomeworkConfigStep: React.FC<
  StepComponentProps<HomeworkIdeaCreateRequestDto>
> = ({ data, onDataChange, themeColor }) => {
  const { t } = useTranslation();
  const [showCustomPurpose, setShowCustomPurpose] = useState(false);
  const [showCustomTime, setShowCustomTime] = useState(false);

  const homeworkPurposeOptions = [
    {
      id: "Review and Reinforce",
      emoji: "🔄",
      label: t("homework.purpose.review", "Review and Reinforce"),
      desc: t(
        "homework.purpose.reviewDesc",
        "Strengthen understanding of previously learned concepts",
      ),
    },
    {
      id: "Practice and Exercise",
      emoji: "💪",
      label: t("homework.purpose.practice", "Practice and Exercise"),
      desc: t(
        "homework.purpose.practiceDesc",
        "Apply skills through repeated practice",
      ),
    },
    {
      id: "Research and Examine",
      emoji: "🔍",
      label: t("homework.purpose.research", "Research and Examine"),
      desc: t(
        "homework.purpose.researchDesc",
        "Investigate and explore new topics",
      ),
    },
    {
      id: "Create and Design",
      emoji: "✨",
      label: t("homework.purpose.create", "Create and Design"),
      desc: t(
        "homework.purpose.createDesc",
        "Build original work and projects",
      ),
    },
    {
      id: "Analyze and Evaluate",
      emoji: "🧠",
      label: t("homework.purpose.analyze", "Analyze and Evaluate"),
      desc: t(
        "homework.purpose.analyzeDesc",
        "Think critically and assess information",
      ),
    },
  ];

  const expectedTimeOptions = [
    {
      id: "1 hour",
      emoji: "🕐",
      label: t("homework.time.hour", "1 hour"),
    },
    {
      id: "1 day",
      emoji: "📅",
      label: t("homework.time.day", "1 day"),
    },
    {
      id: "1 week",
      emoji: "📅",
      label: t("homework.time.week", "1 week"),
    },
    {
      id: "1 month",
      emoji: "🗓️",
      label: t("homework.time.month", "1 month"),
    },
    {
      id: "1 semester",
      emoji: "📚",
      label: t("homework.time.semester", "1 semester"),
    },
  ];

  const difficultyOptions = [
    {
      id: "easy",
      emoji: "😊",
      label: t("homework.difficulty.easy", "Easy"),
      desc: t("homework.difficulty.easyDesc", "Basic level understanding"),
    },
    {
      id: "medium",
      emoji: "🤔",
      label: t("homework.difficulty.medium", "Medium"),
      desc: t("homework.difficulty.mediumDesc", "Moderate challenge level"),
    },
    {
      id: "hard",
      emoji: "🚀",
      label: t("homework.difficulty.hard", "Hard"),
      desc: t("homework.difficulty.hardDesc", "Advanced level challenge"),
    },
  ];

  const homeworkTypeOptions = [
    {
      id: "individual",
      emoji: "👤",
      label: t("homework.type.individual", "Individual Work"),
      desc: t("homework.type.individualDesc", "Students work independently"),
    },
    {
      id: "group",
      emoji: "👥",
      label: t("homework.type.group", "Group Work"),
      desc: t("homework.type.groupDesc", "Students collaborate in teams"),
    },
  ];

  const handlePurposeSelect = (purpose: string) => {
    onDataChange({ homeworkPurpose: purpose });
    setShowCustomPurpose(false);
  };

  const handlePurposeOthersClick = () => {
    onDataChange({ homeworkPurpose: "" });
    setShowCustomPurpose(true);
  };

  const handleTimeSelect = (time: string) => {
    onDataChange({ customDuration: time });
    setShowCustomTime(false);
  };

  const handleTimeOthersClick = () => {
    onDataChange({ customDuration: "" });
    setShowCustomTime(true);
  };

  const handleDifficultySelect = (difficulty: string) => {
    onDataChange({ difficultyLevel: difficulty });
  };

  const handleHomeworkTypeSelect = (type: string) => {
    onDataChange({ workType: type });
  };

  // Helper functions to check if "Others" option should be selected
  const isPurposeOthersSelected = () => {
    return (
      showCustomPurpose ||
      (!homeworkPurposeOptions.some(
        (option) => option.id === data.homeworkPurpose,
      ) &&
        !!data.homeworkPurpose)
    );
  };

  const isTimeOthersSelected = () => {
    return (
      showCustomTime ||
      (!expectedTimeOptions.some(
        (option) => option.id === data.customDuration,
      ) &&
        !!data.customDuration)
    );
  };

  return (
    <div className="space-y-8">
      <div className="text-center mb-6">
        <h2
          className={`text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-${themeColor}-600 to-${themeColor}-800 dark:from-${themeColor}-400 dark:to-${themeColor}-600`}
        >
          {t("homework.steps.config.title", "Homework Configuration")}
        </h2>
        <p className="text-gray-600 dark:text-gray-400 mt-2">
          {t(
            "homework.steps.config.description",
            "Define the purpose, duration, and type of homework",
          )}
        </p>
      </div>

      {/* Homework Purpose Selection */}
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <label
            className={`block text-sm font-medium text-${themeColor}-700 dark:text-${themeColor}-300`}
          >
            {t("homework.form.purpose", "Homework Purpose")}
          </label>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {homeworkPurposeOptions.map((option) => (
            <div
              key={option.id}
              className={`p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 ${
                data.homeworkPurpose === option.id
                  ? `border-${themeColor}-500 bg-${themeColor}-50 dark:bg-${themeColor}-900/20`
                  : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600"
              }`}
              onClick={() => handlePurposeSelect(option.id)}
            >
              <div className="flex items-start gap-3">
                <div
                  className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mt-0.5 ${
                    data.homeworkPurpose === option.id
                      ? `border-${themeColor}-500 bg-${themeColor}-500`
                      : "border-gray-300 dark:border-gray-600"
                  }`}
                >
                  {data.homeworkPurpose === option.id && (
                    <div className="w-2.5 h-2.5 rounded-full bg-white"></div>
                  )}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-lg">{option.emoji}</span>
                    <h3 className="font-medium text-base">{option.label}</h3>
                  </div>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    {option.desc}
                  </p>
                </div>
              </div>
            </div>
          ))}

          {/* Others Option for Purpose */}
          <div
            className={`p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 ${
              isPurposeOthersSelected()
                ? `border-${themeColor}-500 bg-${themeColor}-50 dark:bg-${themeColor}-900/20`
                : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600"
            }`}
            onClick={handlePurposeOthersClick}
          >
            <div className="flex items-start gap-3">
              <div
                className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mt-0.5 ${
                  isPurposeOthersSelected()
                    ? `border-${themeColor}-500 bg-${themeColor}-500`
                    : "border-gray-300 dark:border-gray-600"
                }`}
              >
                {isPurposeOthersSelected() && (
                  <div className="w-2.5 h-2.5 rounded-full bg-white"></div>
                )}
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-lg">✏️</span>
                  <h3 className="font-medium text-base">
                    {t("homework.purpose.others", "Other")}
                  </h3>
                </div>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  {t("homework.purpose.othersDesc", "Specify your own purpose")}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Custom Purpose Textarea */}
      {showCustomPurpose && (
        <div className="space-y-3">
          <label
            className={`block text-sm font-medium text-${themeColor}-700 dark:text-${themeColor}-300`}
          >
            {t("homework.form.customPurpose", "Custom Purpose")}
          </label>
          <textarea
            value={data.homeworkPurpose || ""}
            onChange={(e) => onDataChange({ homeworkPurpose: e.target.value })}
            className={`w-full rounded-xl border-2 border-gray-200 dark:border-gray-700 p-4 text-base focus:ring-2 focus:ring-${themeColor}-500 focus:border-transparent dark:bg-gray-700/50 transition-all duration-200`}
            placeholder={t(
              "homework.form.customPurposePlaceholder",
              "Describe the purpose of this homework...",
            )}
            rows={3}
          />
        </div>
      )}

      {/* Expected Time Selection */}
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <label
            className={`block text-sm font-medium text-${themeColor}-700 dark:text-${themeColor}-300`}
          >
            {t("homework.form.expectedTime", "Expected Time")}
          </label>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {expectedTimeOptions.map((option) => (
            <div
              key={option.id}
              className={`p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 ${
                data.customDuration === option.id
                  ? `border-${themeColor}-500 bg-${themeColor}-50 dark:bg-${themeColor}-900/20`
                  : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600"
              }`}
              onClick={() => handleTimeSelect(option.id)}
            >
              <div className="flex items-center gap-3">
                <div
                  className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                    data.customDuration === option.id
                      ? `border-${themeColor}-500 bg-${themeColor}-500`
                      : "border-gray-300 dark:border-gray-600"
                  }`}
                >
                  {data.customDuration === option.id && (
                    <div className="w-2.5 h-2.5 rounded-full bg-white"></div>
                  )}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-lg">{option.emoji}</span>
                    <h3 className="font-medium text-base">{option.label}</h3>
                  </div>
                </div>
              </div>
            </div>
          ))}

          {/* Others Option for Time */}
          <div
            className={`p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 ${
              isTimeOthersSelected()
                ? `border-${themeColor}-500 bg-${themeColor}-50 dark:bg-${themeColor}-900/20`
                : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600"
            }`}
            onClick={handleTimeOthersClick}
          >
            <div className="flex items-center gap-3">
              <div
                className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                  isTimeOthersSelected()
                    ? `border-${themeColor}-500 bg-${themeColor}-500`
                    : "border-gray-300 dark:border-gray-600"
                }`}
              >
                {isTimeOthersSelected() && (
                  <div className="w-2.5 h-2.5 rounded-full bg-white"></div>
                )}
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-lg">✏️</span>
                  <h3 className="font-medium text-base">
                    {t("homework.time.others", "Other")}
                  </h3>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Custom Time Input */}
      {showCustomTime && (
        <div className="space-y-3">
          <label
            className={`block text-sm font-medium text-${themeColor}-700 dark:text-${themeColor}-300`}
          >
            {t("homework.form.customTime", "Custom Time")}
          </label>
          <input
            type="text"
            value={data.customDuration || ""}
            onChange={(e) => onDataChange({ customDuration: e.target.value })}
            className={`w-full rounded-xl border-2 border-gray-200 dark:border-gray-700 p-4 text-base focus:ring-2 focus:ring-${themeColor}-500 focus:border-transparent dark:bg-gray-700/50 transition-all duration-200`}
            placeholder={t(
              "homework.form.customTimePlaceholder",
              "e.g., 2 hours, 3 days, etc.",
            )}
          />
        </div>
      )}

      {/* Difficulty Selection */}
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <label
            className={`block text-sm font-medium text-${themeColor}-700 dark:text-${themeColor}-300`}
          >
            {t("homework.form.difficulty", "Difficulty")}
          </label>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {difficultyOptions.map((option) => (
            <div
              key={option.id}
              className={`p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 ${
                data.difficultyLevel === option.id
                  ? `border-${themeColor}-500 bg-${themeColor}-50 dark:bg-${themeColor}-900/20`
                  : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600"
              }`}
              onClick={() => handleDifficultySelect(option.id)}
            >
              <div className="flex items-start gap-3">
                <div
                  className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mt-0.5 ${
                    data.difficultyLevel === option.id
                      ? `border-${themeColor}-500 bg-${themeColor}-500`
                      : "border-gray-300 dark:border-gray-600"
                  }`}
                >
                  {data.difficultyLevel === option.id && (
                    <div className="w-2.5 h-2.5 rounded-full bg-white"></div>
                  )}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-lg">{option.emoji}</span>
                    <h3 className="font-medium text-base">{option.label}</h3>
                  </div>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    {option.desc}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Homework Type Selection */}
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <label
            className={`block text-sm font-medium text-${themeColor}-700 dark:text-${themeColor}-300`}
          >
            {t("homework.form.type", "Homework Type")}
          </label>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {homeworkTypeOptions.map((option) => (
            <div
              key={option.id}
              className={`p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 ${
                data.workType === option.id
                  ? `border-${themeColor}-500 bg-${themeColor}-50 dark:bg-${themeColor}-900/20`
                  : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600"
              }`}
              onClick={() => handleHomeworkTypeSelect(option.id)}
            >
              <div className="flex items-start gap-3">
                <div
                  className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mt-0.5 ${
                    data.workType === option.id
                      ? `border-${themeColor}-500 bg-${themeColor}-500`
                      : "border-gray-300 dark:border-gray-600"
                  }`}
                >
                  {data.workType === option.id && (
                    <div className="w-2.5 h-2.5 rounded-full bg-white"></div>
                  )}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-lg">{option.emoji}</span>
                    <h3 className="font-medium text-base">{option.label}</h3>
                  </div>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    {option.desc}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
